import React, { useEffect, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Spinner, Alert,
} from "reactstrap";
import { getProfile, updateDisplayName, syncStoredUser } from "../../services/profileService";
import { needsDisplayName, realNameOf } from "../../helpers/displayName";

// Per account: a shared browser should still ask a second member once.
const ASKED_KEY = (userId) => `displayNamePromptAsked:${userId || "anon"}`;

/**
 * Asks a newly signed-in member to pick a display name.
 *
 * Google gives us their real name, and without a choice that is what appears
 * beside anything they share. This offers an alternative exactly once: asked is
 * asked, whether they saved a name or kept their account name. The profile page
 * is where it gets changed afterwards.
 *
 * "Once" is recorded as soon as the dialog is shown, so it cannot come back if
 * the save lands somewhere unexpected — an older backend that stores the name
 * elsewhere used to make it reappear on every page load.
 */
const DisplayNamePrompt = () => {
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (cancelled || !needsDisplayName(profile)) return;
        if (localStorage.getItem(ASKED_KEY(profile.id)) === "1") return;
        // Mark it asked up front: showing it is the "once", so no later outcome
        // can bring it back.
        localStorage.setItem(ASKED_KEY(profile.id), "1");
        setUser(profile);
        setName(realNameOf(profile));
        setOpen(true);
      } catch {
        // Not signed in, or the profile call failed — nothing to prompt about.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => setOpen(false);

  const save = async () => {
    const chosen = name.trim();
    if (!chosen) {
      setError("Enter a display name, or keep your account name.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await updateDisplayName(chosen);
      syncStoredUser(updated);
      setOpen(false);
      // The header reads the stored session on mount, so reflect the new name.
      window.location.reload();
    } catch (e) {
      // profileService moved to directApi, which throws an Error carrying the
      // server's sentence. The string test this used to do is now always false,
      // so "that name is taken" would have arrived as the generic fallback.
      setError(e?.message || "Could not save that name.");
      setSaving(false);
    }
  };

  if (!open || !user) return null;

  return (
    <Modal isOpen={open} toggle={dismiss} centered>
      <ModalHeader toggle={dismiss}>Choose a display name</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          This is the name shown in the app and beside any research you share.
          Without one, your Google account name{" "}
          <strong>{realNameOf(user)}</strong> is used.
        </p>
        {error && <Alert color="danger">{error}</Alert>}
        <FormGroup className="mb-0">
          <Label for="promptDisplayName">Display name</Label>
          <Input id="promptDisplayName" value={name} maxLength={60} autoFocus
                 placeholder="How you want to appear"
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={dismiss} disabled={saving}>
          Use my account name
        </Button>
        <Button color="primary" onClick={save} disabled={saving || !name.trim()}>
          {saving ? <><Spinner size="sm" /> Saving…</> : "Save name"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DisplayNamePrompt;
